import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import {
  PhoneIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  HandThumbUpIcon,
  FunnelIcon,
  ArrowsUpDownIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';

interface OverdueItem {
  installment_id: string;
  customer_name: string;
  customer_phone: string;
  customer_alt_phone?: string;
  amount_outstanding: string;
  total_amount_due: string;
  days_late: number;
  due_date: string;
  branch_name: string;
  item_desc: string;
  agreement_id: string;
}

interface OverdueSummary {
  total_count: number;
  total_amount: number;
  avg_days_late: number;
  max_days_late: number;
}

interface FilterState {
  days_late_min?: number;
  days_late_max?: number;
  branch_id?: string;
  amount_min?: number;
  amount_max?: number;
  sort_by: 'days_late' | 'total_amount_due' | 'due_date' | 'customer_name';
  sort_order: 'asc' | 'desc';
}

const OverdueCollectionsPage: React.FC = () => {
  const [overdueItems, setOverdueItems] = useState<OverdueItem[]>([]);
  const [summary, setSummary] = useState<OverdueSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterState>({
    sort_by: 'days_late',
    sort_order: 'desc'
  });
  const [showFilters, setShowFilters] = useState(false);
  const [branches, setBranches] = useState<Array<{ id: string; name: string }>>([]);

  // Fetch overdue items
  const fetchOverdueItems = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      if (filters.days_late_min) params.append('days_late_min', filters.days_late_min.toString());
      if (filters.days_late_max) params.append('days_late_max', filters.days_late_max.toString());
      if (filters.branch_id) params.append('branch_id', filters.branch_id);
      if (filters.amount_min) params.append('amount_min', filters.amount_min.toString());
      if (filters.amount_max) params.append('amount_max', filters.amount_max.toString());
      params.append('sort_by', filters.sort_by);
      params.append('sort_order', filters.sort_order);

      const response = await fetch(`/api/overdue?${params}`);
      if (!response.ok) throw new Error('Failed to fetch overdue items');

      const data = await response.json();
      setOverdueItems(data.data);
      setSummary(data.summary);
    } catch (error) {
      console.error('Error fetching overdue items:', error);
      toast.error('Failed to load overdue items');
    } finally {
      setLoading(false);
    }
  };

  // Fetch summary
  const fetchSummary = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.branch_id) params.append('branch_id', filters.branch_id);

      const response = await fetch(`/api/overdue/summary?${params}`);
      if (!response.ok) throw new Error('Failed to fetch summary');

      const data = await response.json();
      setSummary(data.summary);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  // Fetch branches
  const fetchBranches = async () => {
    try {
      const response = await fetch('/api/branches');
      if (!response.ok) throw new Error('Failed to fetch branches');

      const data = await response.json();
      setBranches(data.data || []);
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  // Perform collection action
  const performAction = async (installmentId: string, actionType: string, additionalData?: any) => {
    try {
      setActionLoading(installmentId);

      const response = await fetch(`/api/collections/${installmentId}/action`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action_type: actionType,
          ...additionalData
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Action failed');
      }

      const result = await response.json();

      // Handle different action results
      switch (actionType) {
        case 'call':
          if (result.result.call_url) {
            window.open(result.result.call_url, '_blank');
          }
          toast.success('Call action logged successfully');
          break;

        case 'whatsapp':
          if (result.result.success) {
            toast.success('WhatsApp message sent successfully');
          } else {
            toast.error('WhatsApp failed, fallback URL provided');
            if (result.result.fallback_url) {
              window.open(result.result.fallback_url, '_blank');
            }
          }
          break;

        case 'note':
          toast.success('Note added successfully');
          break;

        case 'promise':
          toast.success('Payment promise created successfully');
          break;
      }

      // Refresh data
      await fetchOverdueItems();
      await fetchSummary();

    } catch (error: any) {
      console.error('Error performing action:', error);
      toast.error(error.message || 'Action failed');
    } finally {
      setActionLoading(null);
    }
  };

  // Handle WhatsApp action
  const handleWhatsApp = (item: OverdueItem) => {
    performAction(item.installment_id, 'whatsapp');
  };

  // Handle call action
  const handleCall = (item: OverdueItem) => {
    const phone = item.customer_alt_phone || item.customer_phone;
    performAction(item.installment_id, 'call', { customer_phone: phone });
  };

  // Handle note action
  const handleNote = (item: OverdueItem) => {
    const note = prompt('Enter note for this collection action:');
    if (note) {
      performAction(item.installment_id, 'note', { note });
    }
  };

  // Handle promise action
  const handlePromise = (item: OverdueItem) => {
    const promiseAmount = prompt('Enter promise amount (LYD):');
    const promiseDate = prompt('Enter promise date (YYYY-MM-DD):');

    if (promiseAmount && promiseDate) {
      const amount = parseFloat(promiseAmount);
      if (isNaN(amount)) {
        toast.error('Invalid amount');
        return;
      }

      performAction(item.installment_id, 'promise', {
        promise_amount: amount,
        promise_date: promiseDate
      });
    }
  };

  // Format currency
  const formatCurrency = (amount: string) => {
    return new Intl.NumberFormat('ar-LY', {
      style: 'currency',
      currency: 'LYD'
    }).format(parseFloat(amount));
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ar-LY');
  };

  // Get days late color
  const getDaysLateColor = (days: number) => {
    if (days <= 7) return 'text-green-600 bg-green-100';
    if (days <= 30) return 'text-yellow-600 bg-yellow-100';
    if (days <= 90) return 'text-orange-600 bg-orange-100';
    return 'text-red-600 bg-red-100';
  };

  useEffect(() => {
    fetchOverdueItems();
    fetchBranches();
  }, [filters]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading overdue items...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Overdue Collections - تحصيل المتأخرات
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Manage and track overdue installments
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <FunnelIcon className="h-4 w-4 mr-2" />
                Filters
              </button>
              <button
                onClick={fetchOverdueItems}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Days Late Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Days Late Min</label>
                <input
                  type="number"
                  value={filters.days_late_min || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, days_late_min: e.target.value ? parseInt(e.target.value) : undefined }))}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Days Late Max</label>
                <input
                  type="number"
                  value={filters.days_late_max || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, days_late_max: e.target.value ? parseInt(e.target.value) : undefined }))}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Branch Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Branch</label>
                <select
                  value={filters.branch_id || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, branch_id: e.target.value || undefined }))}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Branches</option>
                  {branches.map(branch => (
                    <option key={branch.id} value={branch.id}>{branch.name}</option>
                  ))}
                </select>
              </div>

              {/* Sort */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Sort By</label>
                <select
                  value={filters.sort_by}
                  onChange={(e) => setFilters(prev => ({ ...prev, sort_by: e.target.value as any }))}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="days_late">Days Late</option>
                  <option value="total_amount_due">Amount Due</option>
                  <option value="due_date">Due Date</option>
                  <option value="customer_name">Customer Name</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {summary && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <EyeSlashIcon className="h-6 w-6 text-red-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Total Overdue</dt>
                      <dd className="text-lg font-medium text-gray-900">{summary.total_count}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <EyeIcon className="h-6 w-6 text-blue-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Total Amount</dt>
                      <dd className="text-lg font-medium text-gray-900">{formatCurrency(summary.total_amount.toString())}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ArrowsUpDownIcon className="h-6 w-6 text-yellow-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Avg Days Late</dt>
                      <dd className="text-lg font-medium text-gray-900">{summary.avg_days_late}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <EyeSlashIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Max Days Late</dt>
                      <dd className="text-lg font-medium text-gray-900">{summary.max_days_late}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Overdue Items List */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {overdueItems.map((item) => (
              <li key={item.installment_id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <h3 className="text-lg font-medium text-gray-900">
                            {item.customer_name}
                          </h3>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getDaysLateColor(item.days_late)}`}>
                            {item.days_late} days late
                          </span>
                        </div>
                        <p className="mt-1 text-sm text-gray-500">
                          {item.item_desc} • {item.branch_name}
                        </p>
                        <p className="mt-1 text-sm text-gray-500">
                          Due: {formatDate(item.due_date)} • Outstanding: {formatCurrency(item.amount_outstanding)}
                        </p>
                        <p className="mt-1 text-sm text-gray-500">
                          Phone: {item.customer_phone}
                          {item.customer_alt_phone && ` • Alt: ${item.customer_alt_phone}`}
                        </p>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleWhatsApp(item)}
                          disabled={actionLoading === item.installment_id}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                          title="Send WhatsApp"
                        >
                          <ChatBubbleLeftRightIcon className="h-4 w-4" />
                        </button>

                        <button
                          onClick={() => handleCall(item)}
                          disabled={actionLoading === item.installment_id}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                          title="Call Customer"
                        >
                          <PhoneIcon className="h-4 w-4" />
                        </button>

                        <button
                          onClick={() => handleNote(item)}
                          disabled={actionLoading === item.installment_id}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-yellow-600 hover:bg-yellow-700 disabled:opacity-50"
                          title="Add Note"
                        >
                          <DocumentTextIcon className="h-4 w-4" />
                        </button>

                        <button
                          onClick={() => handlePromise(item)}
                          disabled={actionLoading === item.installment_id}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
                          title="Create Promise"
                        >
                          <HandThumbUpIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>

          {overdueItems.length === 0 && (
            <div className="text-center py-12">
              <EyeSlashIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No overdue items</h3>
              <p className="mt-1 text-sm text-gray-500">
                No overdue installments found with the current filters.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OverdueCollectionsPage; 